import { useState, useMemo, useCallback } from 'react'
import { Modal, ModalBody, ModalFooter } from '~/components/modals/CurieModal'
import { Button } from '~/components/common/Button'
import { Typography } from '~/components/common/Typography'
import PasswordInput from '~/components/common/PasswordInput'

const CheckCircleFilled = ({ className }: { className?: string }) => (
  <svg
    width='16'
    height='16'
    viewBox='0 0 16 16'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
    className={className}
  >
    <path
      d='M8 1C4.13438 1 1 4.13438 1 8C1 11.8656 4.13438 15 8 15C11.8656 15 15 11.8656 15 8C15 4.13438 11.8656 1 8 1ZM11.0234 5.71406L7.73281 10.2766C7.68682 10.3408 7.62619 10.3931 7.55595 10.4291C7.48571 10.4652 7.40787 10.4841 7.32891 10.4841C7.24994 10.4841 7.17211 10.4652 7.10186 10.4291C7.03162 10.3931 6.97099 10.3408 6.925 10.2766L4.97656 7.57656C4.91719 7.49375 4.97656 7.37813 5.07812 7.37813H5.81094C5.97031 7.37813 6.12187 7.45469 6.21562 7.58594L7.32812 9.12969L9.78438 5.72344C9.87813 5.59375 10.0281 5.51562 10.1891 5.51562H10.9219C11.0234 5.51562 11.0828 5.63125 11.0234 5.71406Z'
      fill='currentColor'
    />
  </svg>
)

const CloseCircleTwoTone = ({ className }: { className?: string }) => (
  <svg
    width='16'
    height='16'
    viewBox='0 0 16 16'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
    className={className}
  >
    <path
      d='M8 1C4.13438 1 1 4.13438 1 8C1 11.8656 4.13438 15 8 15C11.8656 15 15 11.8656 15 8C15 4.13438 11.8656 1 8 1ZM8 13.8125C4.79063 13.8125 2.1875 11.2094 2.1875 8C2.1875 4.79063 4.79063 2.1875 8 2.1875C11.2094 2.1875 13.8125 4.79063 13.8125 8C13.8125 11.2094 11.2094 13.8125 8 13.8125Z'
      fill='currentColor'
    />
    <path
      d='M10.7156 5.5C10.7156 5.43125 10.6594 5.375 10.5906 5.375L9.55937 5.37969L8.00625 7.23125L6.45469 5.38125L5.42187 5.37656C5.35312 5.37656 5.29688 5.43125 5.29688 5.50156C5.29688 5.53125 5.30781 5.55937 5.32656 5.58281L7.35937 8.00469L5.32656 10.425C5.30768 10.4479 5.29721 10.4766 5.29688 10.5063C5.29687 10.575 5.35312 10.6313 5.42187 10.6313L6.45469 10.6266L8.00625 8.775L9.55781 10.625L10.5891 10.6297C10.6578 10.6297 10.7141 10.575 10.7141 10.5047C10.7141 10.475 10.7031 10.4469 10.6844 10.4234L8.65469 8.00313L10.6875 5.58125C10.7062 5.55938 10.7156 5.52969 10.7156 5.5Z'
      fill='currentColor'
    />
  </svg>
)

interface PasswordRequirement {
  label: string
  test: (password: string) => boolean
}

const PASSWORD_REQUIREMENTS: PasswordRequirement[] = [
  { label: 'At least 8 characters', test: p => p.length >= 8 },
  { label: 'At least one uppercase letter', test: p => /[A-Z]/.test(p) },
  { label: 'At least one lowercase letter', test: p => /[a-z]/.test(p) },
  { label: 'At least one number or special character', test: p => /[\d\W]/.test(p) }
]

const PASSWORD_INPUT_CLASS = 'placeholder:text-tokens-neutral-text-placeholder'

interface ChangePasswordModalProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (oldPassword: string, newPassword: string) => void
}

export function ChangePasswordModal({ isOpen, onClose, onSubmit }: ChangePasswordModalProps) {
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')

  const requirementResults = useMemo(
    () => PASSWORD_REQUIREMENTS.map(req => ({ ...req, met: req.test(newPassword) })),
    [newPassword]
  )

  const allRequirementsMet = requirementResults.every(r => r.met)
  const passwordsMatch = newPassword === confirmPassword && confirmPassword.length > 0
  const isValid = currentPassword.length > 0 && allRequirementsMet && passwordsMatch

  const resetAndClose = useCallback(() => {
    setCurrentPassword('')
    setNewPassword('')
    setConfirmPassword('')
    onClose()
  }, [onClose])

  const handleSubmit = useCallback(() => {
    if (!isValid) return
    onSubmit(currentPassword, newPassword)
  }, [isValid, currentPassword, newPassword, onSubmit])

  return (
    <Modal isOpen={isOpen} onClose={resetAndClose}>
      {/* Header */}
      <div className='flex flex-col items-center pt-[60px]'>
        <Typography fontFamily='geist' fontSize='titleLg' fontWeight='bold'>
          Change Password
        </Typography>
      </div>

      {/* Body */}
      <ModalBody className='px-12 py-12'>
        <div className='flex flex-col gap-12 items-center'>
          {/* Current Password */}
          <div className='flex flex-col gap-2 w-[360px]'>
            <Typography
              fontFamily='geistMono'
              fontSize='labelMd'
              fontWeight='semibold'
              className='text-tokens-neutral-text-secondary'
            >
              Current Password
            </Typography>
            <PasswordInput
              placeholder='Current Password'
              value={currentPassword}
              onChange={e => setCurrentPassword(e.target.value)}
              autoComplete='current-password'
              className={PASSWORD_INPUT_CLASS}
            />
          </div>

          {/* New Password */}
          <div className='flex flex-col gap-2 w-[360px]'>
            <Typography
              fontFamily='geistMono'
              fontSize='labelMd'
              fontWeight='semibold'
              className='text-tokens-neutral-text-secondary'
            >
              Input New Password
            </Typography>
            <PasswordInput
              placeholder='New Password'
              value={newPassword}
              onChange={e => setNewPassword(e.target.value)}
              autoComplete='new-password'
              className={PASSWORD_INPUT_CLASS}
            />
            {newPassword.length > 0 && (
              <div className='flex flex-col gap-2 px-4 pt-1'>
                {requirementResults.map((req, i) => (
                  <div key={i} className='flex items-center gap-2'>
                    {req.met ? (
                      <CheckCircleFilled className='w-4 h-4 shrink-0 aspect-square text-tokens-status-success-2' />
                    ) : (
                      <CloseCircleTwoTone className='w-4 h-4 shrink-0 aspect-square text-tokens-status-error-1' />
                    )}
                    <Typography
                      fontFamily='geist'
                      fontSize='bodySm'
                      className='text-tokens-neutral-text-secondary'
                    >
                      {req.label}
                    </Typography>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Confirm Password */}
          <div className='flex flex-col gap-2 w-[360px]'>
            <Typography
              fontFamily='geistMono'
              fontSize='labelMd'
              fontWeight='semibold'
              className='text-tokens-neutral-text-secondary'
            >
              Confirm Password
            </Typography>
            <PasswordInput
              placeholder='Confirm New Password'
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              autoComplete='new-password'
              className={PASSWORD_INPUT_CLASS}
            />
            {confirmPassword.length > 0 && !passwordsMatch && (
              <Typography
                fontFamily='geist'
                fontSize='bodySm'
                className='text-tokens-status-error-1 px-4'
              >
                Passwords do not match
              </Typography>
            )}
          </div>
        </div>
      </ModalBody>

      {/* Footer */}
      <ModalFooter>
        <Button variant='outlined' onClick={resetAndClose}>
          Cancel
        </Button>
        <Button variant='primary' onClick={handleSubmit} isDisabled={!isValid}>
          Change Password
        </Button>
      </ModalFooter>
    </Modal>
  )
}
